import { ProfileImage } from "./ProfileImage";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Linkedin,
  MessageSquare,
  FileText,
  EyeOff,
  Info,
} from "lucide-react";
import { useState } from "react";
import { PrimaryDetailsForm } from "./PrimaryDetailsForm";
import { useQuery } from "@tanstack/react-query";
import { SharingProfileButton } from "./SharingProfileButton";
import { ExternalLink } from "@/components/Button/ExternalLink";
import { ChatDialog } from "@/features/chat/chat-dialog";
import { useTRPC } from "@/trpc";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const PrimaryDetailsCard = ({
  userId,
  profileImagePath,
  linkedinUrl,
  editable,
  shareable,
  address1,
  address2,
  city,
  state,
  zipCode,
  firstName,
  lastName,
  middleName,
  suffixName,
  specialtyCodes,
  npiNumber,
  isProviderHidden = false,
  healthcareProfessionalType,
  onEditChange,
  isEditing,
  showChatButton,
  onViewDocuments,
}: {
  userId: string;
  profileImagePath: string | undefined | null;
  linkedinUrl: string | null | undefined;
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zipCode: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  middleName: string | undefined;
  suffixName: string | undefined;
  specialtyCodes: string[];
  npiNumber: string | undefined | null;
  isProviderHidden?: boolean;
  healthcareProfessionalType: string | undefined;
  onEditChange: (editing: boolean) => void;
  isEditing: boolean;
  editable: boolean;
  shareable: boolean;
  showChatButton: boolean;
  onViewDocuments: () => void;
}) => {
  const trpc = useTRPC();
  const {
    data: healthcareRoleData,
    isLoading: isLoadingHealthcareRole,
    error: healthcareRoleError,
  } = useQuery(
    trpc.healthcareProfessionalTypes.getById.queryOptions(
      healthcareProfessionalType
        ? { id: healthcareProfessionalType }
        : undefined,
      {
        retry: 2,
        enabled: Boolean(healthcareProfessionalType),
      },
    ),
  );
  const [chattingProvider, setChattingProvider] = useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);

  if (isEditing) {
    return (
      <PrimaryDetailsForm
        userId={userId}
        defaultValues={{
          linkedinUrl: linkedinUrl || "",
          firstName: firstName || "",
          middleName: middleName || "",
          lastName: lastName || "",
          suffixName: suffixName || "",
          specialtyCodes: specialtyCodes || [],
          npiNumber: npiNumber != null ? String(npiNumber) : "",
          address: {
            address1: address1 || "",
            address2: address2 || "",
            city: city || "",
            state: state || "",
            zipCode: zipCode || "",
          },
          healthcareProfessionalType: healthcareProfessionalType || "",
        }}
        onSubmit={() => onEditChange(false)}
        onCancel={() => onEditChange(false)}
      />
    );
  }

  return (
    <div className="flex flex-col items-start gap-4 sm:flex-row">
      <ProfileImage
        editable={editable}
        userId={userId}
        profileImagePath={profileImagePath}
        firstName={firstName}
        lastName={lastName}
      />
      <div className="w-full min-w-0 flex-1">
        <div className="flex justify-between flex-col sm:flex-row">
          <div className="pr-2">
            <div className="flex items-center gap-2 mb-2">
              {isProviderHidden ? (
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-semibold text-gray-500 flex items-center gap-2">
                    <EyeOff className="h-5 w-5" />
                    Provider #{userId.slice(-4)}
                  </h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-3" side="bottom">
                      <p className="text-sm text-gray-600">
                        This provider's real name is hidden. To see their full
                        identity, NPI, and documents, use the{" "}
                        <span className="font-medium">Reveal</span> button.
                        Revealing is permanent and cannot be undone.
                      </p>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold">
                    {firstName ? (
                      <>
                        {String(firstName || "")} {String(middleName || "")}{" "}
                        {String(lastName || "")} {String(suffixName || "")}
                      </>
                    ) : (
                      "Not provided"
                    )}
                  </h2>
                  {editable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      tabIndex={0}
                      className="hover:text-gray-700"
                      onClick={() => onEditChange(true)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
            {healthcareRoleData && (
              <p className="font-semibold">{healthcareRoleData.name}</p>
            )}
            {isLoadingHealthcareRole && healthcareProfessionalType && (
              <p className="text-sm text-gray-500">Loading provider type...</p>
            )}
            {healthcareRoleError && healthcareProfessionalType && (
              <p className="text-sm text-red-600">
                Unable to load provider type.
              </p>
            )}
            {city && state && (
              <p className="text-sm font-semibold">
                {city}, {state}
              </p>
            )}
            {isProviderHidden ? (
              <div className="flex items-center gap-1">
                <p className="text-sm text-gray-400">NPI: Hidden</p>
              </div>
            ) : (
              <div className="flex gap-2">
                <p className="text-sm">
                  NPI: {String(npiNumber) || "Not provided"}
                </p>
              </div>
            )}
          </div>
          <div className="flex flex-row gap-2 mt-4 sm:mt-0 flex-wrap items-start sm:gap-y-0">
            {showChatButton && !isProviderHidden && (
              <Button
                size="sm"
                className="flex items-center gap-1"
                onClick={() => {
                  setChattingProvider({
                    id: userId,
                    firstName: firstName || "",
                    lastName: lastName || "",
                  });
                }}
              >
                <MessageSquare className="h-4 w-4" />
                <span>Chat</span>
              </Button>
            )}
            {linkedinUrl && !isProviderHidden && (
              <ExternalLink
                href={linkedinUrl || ""}
                className="border border-gray-200 rounded-md px-2 py-1 h-9 inline-flex items-center hover:bg-gray-100"
              >
                <Linkedin className="h-4 w-4 mr-2" />
                LinkedIn
              </ExternalLink>
            )}
            <div className="flex w-full flex-col gap-2 sm:w-auto">
              {shareable && !isProviderHidden && (
                <SharingProfileButton userId={userId} />
              )}
              {!isProviderHidden && (
                <Button
                  variant="outline"
                  className="w-full gap-2 font-semibold"
                  onClick={onViewDocuments}
                >
                  <FileText aria-hidden="true" className="size-4" />
                  View documents
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {chattingProvider && (
        <ChatDialog
          isOpen={chattingProvider !== null}
          onOpenChange={() => setChattingProvider(null)}
          user={chattingProvider}
        />
      )}
    </div>
  );
};
